<template>
	<InstructionPopoverButton
		v-if="!weekGoals.length"
		:label="$tc('Week goal', 2)"
		:instruction="$t('No week goals have been set yet.')"
		:CTAText="$t('Learn more about getting started with week goals')"
		@clickCTA="$emit('clickLearnMore')"
	/>

	<div v-else class="relative inline-block">
		<button type="button" class="btn btn-sm btn-outline" @click="$refs.menu.open()">
			{{ $tc('Week goal', 2) }}
			<div class="badge badge-sm">{{ parentCount }}</div>
			<ChevronDownIcon class="w-4 h-4 ml-1" />
		</button>

		<!-- Flat checkable list (no per-curriculum grouping, spec 2.3). Toggles emit
		     immediately; the parent applies optimistically and reverts on API error.
		     The dropdown stays open for multi-ticking. -->
		<Dropdown ref="menu" position="bottom-start" class="w-[28rem] max-w-[90vw]" teleport>
			<div class="bg-base rounded-box shadow-lg border border-base-200 p-2 max-h-96 overflow-y-auto">
				<ul class="flex flex-col gap-1">
					<li v-for="goal in rows" :key="goal.id">
						<GoalListItem
							:listItem="goal"
							action="select"
							:checked="isSelected(goal)"
							:withTags="true"
							@checked="handleChecked(goal)"
							@unchecked="handleUnchecked(goal)"
						/>
					</li>
				</ul>
			</div>
		</Dropdown>
	</div>
</template>

<script lang="ts">
import Dropdown from "@/components/ui/Dropdown.v2.vue";
import InstructionPopoverButton from "@/components/ui/InstructionPopoverButton.vue";
import GoalListItem from "@/components/GoalListItem.vue";
import { Goal } from "@/models/Goal.model";
import { ChevronDownIcon } from "@heroicons/vue/24/solid";
import { groupSubgoalsUnderParents } from "@/goals/utils/SubgoalRows.util";
import { countParentGoals } from "@/models/Curriculum.model";

export default {
	emits: ['goalSelected', 'goalUnselected', 'clickLearnMore'],

	components: {
		Dropdown,
		InstructionPopoverButton,
		ChevronDownIcon,
		GoalListItem,},

	props: {
		weekGoals: {
			type: Array,
			default: () => [],
		},

		selectedGoals: {
			type: Array,
			default: () => [],
		},
	},

	computed: {
		/** Parents first, each followed by its own subgoals — same order as the other goal lists. */
		rows(): Goal[] {
			return groupSubgoalsUnderParents(this.weekGoals as Goal[]);
		},

		parentCount(): number {
			return countParentGoals(this.weekGoals as Goal[]);
		},
	},

	methods: {
		isSelected(goal: Goal): boolean {
			const selected = (this.selectedGoals as Goal[]).find((g) => g.id === goal.id);
			if (!selected) {
				return false;
			}
			// A subgoal's box shows whether the plan's copy is included (unmuted).
			return goal.isSubgoal() ? !selected.muted : true;
		},

		/** The theme's subgoals of a parent goal (same source, parent path equal to its full path). */
		subgoalsOf(parent: Goal): Goal[] {
			if (parent.isSubgoal()) {
				return [];
			}
			return (this.weekGoals as Goal[]).filter((g) =>
				g.isSubgoal()
				&& (g.source ?? '').toLowerCase() === (parent.source ?? '').toLowerCase()
				&& g.sourceParentPath === parent.fullPath
			);
		},

		/** The theme's parent goal of a subgoal, when it is part of the week goals. */
		parentOf(subgoal: Goal): Goal | null {
			return (this.weekGoals as Goal[]).find((g) =>
				!g.isSubgoal()
				&& (g.source ?? '').toLowerCase() === (subgoal.source ?? '').toLowerCase()
				&& g.fullPath === subgoal.sourceParentPath
			) ?? null;
		},

		/** A parent brings its subgoals along, keeping the theme's muted state on each.
		 *  Ticking a subgoal includes it (unmuted); its parent comes along if absent. */
		handleChecked(goal: Goal) {
			if (goal.isSubgoal()) {
				const parent = this.parentOf(goal);
				if (parent && !this.isSelected(parent)) {
					this.$emit('goalSelected', parent);
					this.subgoalsOf(parent).forEach((subgoal) => {
						const copy = subgoal.clone();
						if (subgoal.id === goal.id) {
							copy.muted = false;
						}
						this.$emit('goalSelected', copy);
					});
					return;
				}
				const copy = goal.clone();
				copy.muted = false;
				this.$emit('goalSelected', copy);
				return;
			}

			this.$emit('goalSelected', goal);
			this.subgoalsOf(goal).forEach((subgoal) => this.$emit('goalSelected', subgoal));
		},

		/** Unticking a subgoal keeps it on the plan but muted; unticking a parent removes all. */
		handleUnchecked(goal: Goal) {
			if (goal.isSubgoal()) {
				const copy = goal.clone();
				copy.muted = true;
				this.$emit('goalSelected', copy);
				return;
			}
			this.$emit('goalUnselected', goal);
			this.subgoalsOf(goal).forEach((subgoal) => this.$emit('goalUnselected', subgoal));
		},
	},
};
</script>
